import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useProjects } from '../../../../hooks/useProjects';

import { UnauthorizedLoadingPage } from '../../../../pages/UnauthorizedLoadingPage';
import { useAuth } from '../../../../hooks/useAuth';
import useSessionStorageProject from './useSessionStorageProject';
import useVerifyUserAccess from '../../../../contexts/Forge/hooks/useVerifyUserAccess';
import { useDispatch } from 'react-redux';
import { getProjectBySourceId } from '../../../../actions';

export default function SetProjectSourceIdFromRoute() {
	const { projectSourceId } = useParams<{ projectSourceId: string }>();
	const history = useHistory();
	const {
		injectProjectBySourceId,
		projects,
		initializeProjects,
		isFetching,
		isInitialized,
		isProjectByIdInitialized,
	} = useProjects();
	const { redirectToAutodesk, hasValidToken } = useAuth();
	const { handleFailedInitialization } = useVerifyUserAccess();
	const dispatch = useDispatch();

	const { setSource: setProject } = useSessionStorageProject();

	const handleProjectInjectionAuthentication = React.useCallback(
		(sourceId: string) => {
			if (!hasValidToken()) {
				localStorage.setItem('forge.auth.sourceId', sourceId);
				redirectToAutodesk();
			}
		},
		[hasValidToken, redirectToAutodesk],
	);

	React.useEffect(() => {
		handleProjectInjectionAuthentication(projectSourceId);
	}, []);

	React.useEffect(() => {
		// If we arrive at this page without a code, route to root
		if (!projectSourceId) {
			console.log('could not pull projectSourceId from route');
			history.replace('/');
			return;
		}
		// Initialize projects if not loaded
		if (!isInitialized) {
			initializeProjects();
			return;
		}
		if (isFetching) return; // Wait until fetching is done

		// If we have projects loaded, try to find the one matching the sourceId
		const existingProject = projects?.find(
			project => project.sourceId === projectSourceId,
		);
		if (!existingProject && !isProjectByIdInitialized) {
			// If we have no matching project, try fetching it by sourceId
			dispatch(getProjectBySourceId(projectSourceId));
			return;
		}

		// If we still have no matching project, and we have already tried fetching by sourceId, then the project does not exist or we have no access to it
		if (!existingProject && isProjectByIdInitialized) {
			handleFailedInitialization();
			return;
		}
		// Happy path - we have a project loaded
		if (existingProject) {
			// Workaround for feature toggles not being loaded
			// TODO: Remove this once product picker toggle is removed
			localStorage.setItem('projectFlow', 'true');
			setProject(projectSourceId);
			injectProjectBySourceId(projectSourceId);
			setTimeout(() => {
				history.replace('/');
			}, 300);
		}
	}, [
		projectSourceId,
		projects?.length,
		isInitialized,
		isFetching,
		isProjectByIdInitialized,
	]);

	return <UnauthorizedLoadingPage />;
}
